import { supabase } from '../lib/supabase';
import { GetEntriesResponse, JournalEntry, JournalContent } from '@common/models/journal';
import { Card, CardDeck } from '@common/models/cards';
import { CreateDeckRequest, GetCardDecksResponse, GetDeckCardsResponse } from '@common/models/api';
import { GetSrsCardsResponse, SrsAnswerRequest, SrsAnswerResponse } from '@common/models/srs';

// ApiEntry is not in common models, keeping it local for now
interface ApiEntry {
    id: string;
    user_id: string;
    content: string;
    created_at: string;
}

const BASE_URL = window.location.hostname.includes('localhost')
    ? 'http://localhost:8787'
    : 'https://manichi-api.lucas-ae7.workers.dev';

export async function getEntries(): Promise<{ entries: JournalEntry[] }> {
    const userId = (await supabase.auth.getSession()).data.session?.user?.id;
    if (!userId) {
        throw new Error('User ID not found');
    }
    const response: GetEntriesResponse = await call('GET', `/entries/${userId}/list`);

    return response;
}

export function addEntry(userId: string, entry: JournalContent): Promise<JournalEntry> {
    return call('POST', `/entries/${userId}/create`, entry);
}

export async function deleteEntry(entryId: string) {
    const userId = (await supabase.auth.getSession()).data.session?.user?.id;
    if (!userId) {
        throw new Error('User ID not found');
    }
    return call('DELETE', `/entries/${userId}/${entryId}`);
}

export async function addFeedback(entryId: string, feedback: string): Promise<{ entry: ApiEntry }> {
    return call('POST', `/entries/${entryId}/feedback`, { feedback });
}

// Deck API methods
export function getDecks(): Promise<GetCardDecksResponse> {
    return call('GET', '/decks');
}

export function createDeck(deck: CreateDeckRequest): Promise<CardDeck> {
    return call('POST', '/decks', deck);
}

export function updateDeck(deckId: string, deck: CreateDeckRequest): Promise<CardDeck> {
    return call('PUT', `/decks/${deckId}`, deck);
}

export function deleteDeck(deckId: string): Promise<{ status: string }> {
    return call('DELETE', `/decks/${deckId}`);
}

// Card API methods
export function getCards(deckId: string): Promise<GetDeckCardsResponse> {
    return call('GET', `/cards?deck_id=${deckId}`);
}

export function createCard(card: Omit<Card, 'id' | 'created_at' | 'type'>): Promise<Card> {
    return call('POST', '/cards', card);
}

export function updateCard(cardId: string, card: Pick<Card, 'front' | 'back'>): Promise<Card> {
    return call('PUT', `/cards/${cardId}`, card);
}

export function deleteCard(cardId: string): Promise<{ status: string }> {
    return call('DELETE', `/cards/${cardId}`);
}

// SRS API methods
export function getSrsCards(params: {
    deck_ids: string[];
    limit?: number;
    daily_new_max?: number;
}): Promise<GetSrsCardsResponse> {
    const searchParams = new URLSearchParams({
        deck_ids: params.deck_ids.join(','),
        ...(params.limit && { limit: params.limit.toString() }),
        ...(params.daily_new_max && { daily_new_max: params.daily_new_max.toString() }),
    });
    return call('GET', `/srs/next?${searchParams}`);
}

export function submitSrsAnswer(answer: SrsAnswerRequest): Promise<SrsAnswerResponse> {
    return call('POST', '/srs/answer', answer);
}

async function call(method: string, path: string, body?: any) {
    const {
        data: { session },
    } = await supabase.auth.getSession();
    const authToken = session?.access_token;

    const url = `${BASE_URL}${path}`;
    const response = await fetch(url, {
        method,
        headers: {
            'Content-Type': 'application/json',
            'x-auth-token': authToken,
        },
        body: body ? JSON.stringify(body) : undefined,
    });
    if (!response.ok) {
        throw new Error(`Error: ${response.statusText}`);
    }
    return response.json();
}
